import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import type { Ota } from 'frontend-utils/models';
import { OTA_IDS } from 'frontend-utils/utils/constants/constant-values';
import type { AiUsage, Review } from 'lumen/models';
import type HotelReviewsRoute from 'lumen/routes/hotel/reviews';
import type { OtaError } from 'lumen/types/review-manager';
import type { ModelFrom } from 'lumen/utils/routes';

interface ReviewStatusOption {
  id: string;
  label: string;
}

interface ScoreRangeOption {
  id: string;
  label: string;
}

interface ChannelOption {
  id: string;
  label: string;
  otaId: string;
  propertyId: string;
}

const OTA_ERROR_CODE_EXPEDIA_THIRD_PARTY_SHARING_DISABLED =
  'ota.expedia.third_party_sharing_disabled';

const REVIEW_STATUS = {
  TO_REPLY: 'toreply',
  NO_ACTION_REQUIRED: 'noactionreq',
} as const;

export default class HotelReviewsController extends Controller {
  declare model: ModelFrom<HotelReviewsRoute>;

  queryParams = [
    'selectedChannelIds',
    'selectedReviewStatusIds',
    'selectedScoreRangeIds',
  ];

  @service declare intercom: Services['intercom'];
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked selectedChannelIds: string[] = [];
  @tracked selectedReviewId: string = '';
  @tracked selectedReviewStatusIds: string[] = [REVIEW_STATUS.TO_REPLY];
  @tracked selectedScoreRangeIds: string[] = [];

  private readonly scoreRangeMap: Record<string, (score: number) => boolean> = {
    superb: (score) => score >= 9,
    good: (score) => score >= 7 && score < 9,
    average: (score) => score >= 5 && score < 7,
    poor: (score) => score >= 3 && score < 5,
    verypoor: (score) => score >= 1 && score < 3,
  };

  get aiUsageData(): AiUsage | null {
    return this.model.requestAiUsageTask?.lastSuccessful?.value ?? null;
  }

  @cached
  get channelFilterOptions(): ChannelOption[] {
    return this.model.connectedOtas.map((ota) => ({
      id: ota.id!,
      label: ota.name,
      otaId: ota.otaId,
      propertyId: ota.propertyId,
    }));
  }

  get effectiveSelectedReviewId(): string {
    if (this.selectedReviewId) {
      const isInFilteredList = this.reviewsDataToDisplay.some(
        (review) => review.id === this.selectedReviewId,
      );
      if (isInFilteredList) {
        return this.selectedReviewId;
      }
    }
    return this.reviewsDataToDisplay[0]?.id ?? '';
  }

  /**
   * Returns the Expedia "third_party_sharing_disabled" OTA error if present,
   * indicating the hotel has not enabled the Reviews service in their
   * Expedia extranet. Only returns the error when the user has explicitly
   * filtered the channel dropdown to Expedia only.
   */
  get expediaThirdPartySharingError(): OtaError | undefined {
    const otaErrors =
      this.model.requestReviewsTask.lastSuccessful?.value?.meta?.ota_errors;

    const error = otaErrors?.find(
      (e) => e.code === OTA_ERROR_CODE_EXPEDIA_THIRD_PARTY_SHARING_DISABLED,
    );

    if (!error) {
      return undefined;
    }

    const isExpediaSelected =
      this.selectedChannelIds.length > 0 &&
      this.selectedChannelOptions.every(
        (option) => option.otaId === OTA_IDS.EXPEDIA,
      );

    return isExpediaSelected ? error : undefined;
  }

  get hasNoConnectedOtas(): boolean {
    return this.model.connectedOtas.length === 0;
  }

  get isRefreshDisabled(): boolean {
    return this.hasNoConnectedOtas || this.isRequestingPageData;
  }

  get isRequestingAiUsage(): boolean {
    return this.model.requestAiUsageTask?.isRunning ?? false;
  }

  get isRequestingPageData(): boolean {
    if (this.hasNoConnectedOtas) {
      return false;
    }
    return this.isRequestingReviews || this.isRequestingAiUsage;
  }

  get isRequestingReviews(): boolean {
    return this.model.requestReviewsTask.isRunning;
  }

  get isToReplyStatusFilterSelected(): boolean {
    return (
      this.selectedReviewStatusIds.length === 1 &&
      this.selectedReviewStatusIds.includes(REVIEW_STATUS.TO_REPLY)
    );
  }

  get otasWithFetchErrors(): string[] {
    const otaErrors =
      this.model.requestReviewsTask.lastSuccessful?.value?.meta?.ota_errors;
    if (!otaErrors) {
      return [];
    }

    return otaErrors.map(
      (error) =>
        this.store.peekRecord<Ota>('ota', error.ota_id)?.name ?? error.ota_id,
    );
  }

  @cached
  get reviewsDataToDisplay(): Review[] {
    const filteredReviews = this.filterReviews(
      this.model.requestReviewsTask.lastSuccessful?.value?.reviews ?? [],
    );
    return this.sortReviewsByDate(filteredReviews);
  }

  @cached
  get reviewStatusOptions(): ReviewStatusOption[] {
    return [
      {
        label: this.intl.t('review_manager.review_status_filter.to_reply'),
        id: REVIEW_STATUS.TO_REPLY,
      },
      {
        label: this.intl.t(
          'review_manager.review_status_filter.no_action_needed',
        ),
        id: REVIEW_STATUS.NO_ACTION_REQUIRED,
      },
    ];
  }

  @cached
  get scoreRangeFilterOptions(): ScoreRangeOption[] {
    return this.scoreRangeOptions.map((option) => ({
      id: option.id,
      label: option.label,
    }));
  }

  @cached
  get scoreRangeOptions(): ScoreRangeOption[] {
    return [
      {
        label: this.intl.t('review_manager.score_range_filter.superb'),
        id: 'superb',
      },
      {
        label: this.intl.t('review_manager.score_range_filter.good'),
        id: 'good',
      },
      {
        label: this.intl.t('review_manager.score_range_filter.average'),
        id: 'average',
      },
      {
        label: this.intl.t('review_manager.score_range_filter.poor'),
        id: 'poor',
      },
      {
        label: this.intl.t('review_manager.score_range_filter.very_poor'),
        id: 'verypoor',
      },
    ];
  }

  @cached
  get selectedChannelOptions(): ChannelOption[] {
    const isAllChannelsSelected = this.selectedChannelIds.length === 0;

    return this.channelFilterOptions.filter(
      (option) =>
        isAllChannelsSelected || this.selectedChannelIds.includes(option.id),
    );
  }

  get selectedReview(): Review | null {
    return (
      this.reviewsDataToDisplay.find(
        (review) => review.id === this.effectiveSelectedReviewId,
      ) ?? null
    );
  }

  get selectedReviewStatusOption(): ReviewStatusOption[] {
    const isAllReviewStatusesSelected =
      this.selectedReviewStatusIds.length === 0;

    return this.reviewStatusOptions.filter(
      (option) =>
        isAllReviewStatusesSelected ||
        this.selectedReviewStatusIds.includes(option.id),
    );
  }

  get selectedScoreRangeOption(): ScoreRangeOption[] {
    const isAllScoreRangesSelected = this.selectedScoreRangeIds.length === 0;

    return this.scoreRangeOptions.filter(
      (option) =>
        isAllScoreRangesSelected ||
        this.selectedScoreRangeIds.includes(option.id),
    );
  }

  private filterReviews(reviews: Review[]): Review[] {
    return reviews.filter((review) => {
      // Filter by channels — channel options are keyed per (otaId, propertyId)
      // so multi-property hotels can filter to a specific property.
      if (this.selectedChannelIds.length > 0) {
        const matchesSelectedChannel = this.selectedChannelOptions.some(
          (option) =>
            option.otaId === review.otaId &&
            option.propertyId === review.propertyId,
        );
        if (!matchesSelectedChannel) {
          return false;
        }
      }

      // Filter by score ranges
      if (
        this.selectedScoreRangeIds.length > 0 &&
        !this.matchesScoreRange(review.score)
      ) {
        return false;
      }

      // Filter by review statuses
      if (
        this.selectedReviewStatusIds.length > 0 &&
        !this.matchesReviewStatus(review)
      ) {
        return false;
      }

      return true;
    });
  }

  private matchesReviewStatus(review: Review): boolean {
    const requiresAction =
      !review.isBookingdotcomScoreOnly &&
      !review.hasReply &&
      !review.isPendingApproval;

    return this.selectedReviewStatusIds.some((statusId) => {
      if (statusId === REVIEW_STATUS.TO_REPLY) {
        return requiresAction;
      }
      if (statusId === REVIEW_STATUS.NO_ACTION_REQUIRED) {
        return !requiresAction;
      }
      return false;
    });
  }

  private matchesScoreRange(score: number): boolean {
    return this.selectedScoreRangeIds.some(
      (rangeId) => this.scoreRangeMap[rangeId]?.(score) ?? false,
    );
  }

  private sortReviewsByDate(reviews: Review[]): Review[] {
    return reviews.sort((a, b) => {
      return (
        b.localizedCreatedTimestamp.valueOf() -
        a.localizedCreatedTimestamp.valueOf()
      );
    });
  }

  @action
  channelBuildLabelWithCount(count: number): string {
    if (count === 0) {
      return this.intl.t('review_manager.filter.no_channel_selected');
    }
    return this.intl.t('review_manager.filter.channels_selected', { count });
  }

  @action
  contactCustomerCare(): void {
    this.intercom.showNewMessage(
      this.intl.t('review_agent.no_channels.support_message'),
    );
  }

  @action
  onChannelChange(options: ChannelOption[]): void {
    const selectedChannelIds = options.map((option) => option.id);

    this.usageAnalytics.track('Filter Applied', {
      eventTrigger: 'filter-bar',
      product: 'review-manager',
      filterValue: selectedChannelIds,
      filterType: 'channel',
    });

    this.setSelectedChannels(selectedChannelIds);
  }

  @action
  onReviewsRefreshClicked(): void {
    if (this.hasNoConnectedOtas) {
      return;
    }

    void this.model.requestReviewsTask.perform();

    this.usageAnalytics.track('Reviews Refreshed', {
      eventTrigger: 'refresh-button',
      product: 'review-manager',
    });
  }

  @action
  onReviewStatusChange(options: ReviewStatusOption[]): void {
    const selectedReviewStatusIds = options.map((option) => option.id);

    this.usageAnalytics.track('Filter Applied', {
      eventTrigger: 'filter-bar',
      product: 'review-manager',
      filterValue: selectedReviewStatusIds,
      filterType: 'review_status',
    });

    this.setSelectedReviewStatuses(selectedReviewStatusIds);
  }

  @action
  onScoreRangeChange(options: ScoreRangeOption[]): void {
    const selectedScoreRangeIds = options.map((option) => option.id);

    this.usageAnalytics.track('Filter Applied', {
      eventTrigger: 'filter-bar',
      product: 'review-manager',
      filterValue: selectedScoreRangeIds,
      filterType: 'score_range',
    });

    this.setSelectedScoreRanges(selectedScoreRangeIds);
  }

  @action
  openExpediaExtranet(): void {
    const url = this.expediaThirdPartySharingError?.extranet_url;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  @action
  refreshAiUsage(): void {
    if (this.model.requestAiUsageTask !== undefined) {
      void this.model.requestAiUsageTask.perform();
    }
  }

  @action
  reviewStatusBuildLabelWithCount(count: number): string {
    if (count === 0) {
      return this.intl.t('review_manager.filter.no_review_status_selected');
    }
    return this.intl.t('review_manager.filter.review_statuses_selected', {
      count,
    });
  }

  @action
  scoreRangeBuildLabelWithCount(count: number): string {
    if (count === 0) {
      return this.intl.t('review_manager.filter.no_score_range_selected');
    }
    return this.intl.t('review_manager.filter.score_ranges_selected', {
      count,
    });
  }

  @action
  selectReviewForDetail(reviewId: string): void {
    if (reviewId === this.selectedReviewId) {
      return;
    }

    this.selectedReviewId = reviewId;

    this.usageAnalytics.track('Review Inspected', {
      eventTrigger: 'review-list',
      product: 'review-manager',
    });
  }

  @action
  setSelectedChannels(value: string[]): void {
    const allChannelsSelected =
      value.length === this.channelFilterOptions.length;
    this.selectedChannelIds = allChannelsSelected ? [] : value;
  }

  @action
  setSelectedReviewStatuses(value: string[]): void {
    const allReviewStatusesSelected =
      value.length === this.reviewStatusOptions.length;
    this.selectedReviewStatusIds = allReviewStatusesSelected ? [] : value;
  }

  @action
  setSelectedScoreRanges(value: string[]): void {
    const allScoreRangesSelected =
      value.length === this.scoreRangeOptions.length;
    this.selectedScoreRangeIds = allScoreRangesSelected ? [] : value;
  }
}
